import type { PairAccountingPair } from '../services/pairTradingApi';

/**
 * Live unrealized PnL of the ACTUAL open position, per instrument.
 *
 * The accounting endpoint (`/pair/.../accounting`) already returns a `pnl` per
 * leg, but it is marked to the price the BE happened to read when the request
 * was served. Re-marking the same legs to the websocket price is what makes the
 * number move with the market between polls.
 *
 * Unit rule ([[feedback-vnd-units]]) — the leg fields come from
 * pair-accounting.service.ts and are NOT in one unit:
 *   stock   → `costPrice` is raw VND/share, live price is thousand VND ⇒ ×1000
 *   futures → `costPrice` is index points, live price is points ⇒ ×100000
 * Everything returned here is raw VND.
 */

const FUTURE_MULTIPLIER = 100000;
const STOCK_UNIT = 1000;

export interface PositionPnlLeg {
    /** Open size still on the book, summed across pairs. */
    quantity: number;
    /** Weighted average entry, in the leg's own display unit (VND/share or points). */
    averageCost: number;
    side: 'LONG' | 'SHORT';
    /** Raw VND. Falls back to the BE's snapshot PnL when no live price arrived. */
    pnl: number;
    /** False when the fallback above was used — i.e. not marked to a live tick. */
    live: boolean;
}

/** Bare ticker of an exchange-qualified instrument id. */
const tickerOf = (instrument: string): string => {
    const idx = instrument.indexOf(':');
    return idx >= 0 ? instrument.slice(idx + 1) : instrument;
};

const isFutures = (instrument: string): boolean => instrument.startsWith('HNXDS:');

/**
 * @param pairs      open pairs from the accounting endpoint
 * @param priceOf    live price in the instrument's own display unit (stock:
 *                   thousand VND, futures: index points); null when unknown
 * @returns          one entry per ticker that still has an open position
 */
export function computePositionPnl(
    pairs: PairAccountingPair[],
    priceOf: (ticker: string) => number | null,
): Record<string, PositionPnlLeg> {
    const out: Record<string, PositionPnlLeg & { costTotal: number }> = {};

    for (const pair of pairs) {
        for (const leg of pair.legs) {
            if (!leg?.instrument || !leg.quantity) continue;
            const ticker = tickerOf(leg.instrument);
            const future = isFutures(leg.instrument);
            const sign = leg.side === 'SHORT' ? -1 : 1;

            const live = priceOf(ticker);
            const unitValueVnd =
                live != null && live > 0 ? live * (future ? FUTURE_MULTIPLIER : STOCK_UNIT) : null;
            const costVnd = leg.costPrice * (future ? FUTURE_MULTIPLIER : 1);
            const pnl =
                unitValueVnd != null
                    ? sign * leg.quantity * (unitValueVnd - costVnd)
                    : // No tick for this instrument yet — keep the BE's own mark
                      // rather than dropping the row to zero.
                      leg.pnl ?? 0;

            const acc = out[ticker] ?? {
                quantity: 0,
                averageCost: 0,
                side: leg.side,
                pnl: 0,
                live: true,
                costTotal: 0,
            };
            acc.quantity += leg.quantity;
            acc.costTotal += leg.costPrice * leg.quantity;
            acc.pnl += pnl;
            acc.live = acc.live && unitValueVnd != null;
            // A pair is one-sided per instrument; if two pairs ever disagree the
            // larger size wins the label rather than silently mixing sides.
            acc.side = leg.side;
            out[ticker] = acc;
        }
    }

    const result: Record<string, PositionPnlLeg> = {};
    for (const [ticker, acc] of Object.entries(out)) {
        if (acc.quantity === 0) continue;
        result[ticker] = {
            quantity: acc.quantity,
            averageCost: acc.costTotal / acc.quantity,
            side: acc.side,
            pnl: acc.pnl,
            live: acc.live,
        };
    }
    return result;
}
